/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import ExcelJS from "exceljs";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import { capitalizeWords } from "../../assets/helper.js";

export const PAYMENT_METHOD_OPTIONS = [
  "Bank Transfer",
  "Cheque",
  "Cash",
  "FPS",
  "PayPal",
  "Alipay",
  "AlipayHK (Hong Kong)",
  "Alipay (China)",
  "WeChat Pay",
  "WeChat Pay HK (Hong Kong)",
  "WeChat Pay (China)",
  "BoC Pay",
  "Octopus",
];

export const GENERAL_DONATION = "General Donation";
export const GENERAL_PAYMENT = "General Payment";

export const BASE_HEADERS = [
  "Transaction Date",
  "Data Entry Date",
  "Payer Type",
  "First Name",
  "Last Name",
  "Organisation Name",
  "Email Address",
  "Country Code",
  "Phone Number",
  "Country or Region",
  "Project",
  "Payment Method",
  "Payment Status",
  "Amount",
  "Transaction Fee",
  "Net",
  "Payment Reference",
  "Note",
];

export const PAYER_TYPE_OPTIONS = ["Individual", "Organization"];
export const PAYMENT_STATUS_OPTIONS = ["Completed", "Pending"];

// Country codes: full list derived from libphonenumber-js so the bulk
// template matches the manual form (react-phone-number-input) exactly.
// HK is pinned to the top for quick access; everything else is sorted
// alphabetically by country name.
const PRIORITY_COUNTRIES = ["HK", "CN", "MO", "TW"];

const getCountryName = (iso) => {
  try {
    if (typeof Intl !== "undefined" && Intl.DisplayNames) {
      const dn = new Intl.DisplayNames(["en"], { type: "region" });
      return dn.of(iso) || iso;
    }
  } catch (e) { /* fall through */ }
  return iso;
};

const buildCountryCodeOptions = () => {
  const all = getCountries()
    .map((iso) => {
      try {
        return {
          iso,
          name: getCountryName(iso),
          code: "+" + getCountryCallingCode(iso),
        };
      } catch (err) {
        return null;
      }
    })
    .filter(Boolean);

  const priority = PRIORITY_COUNTRIES
    .map((iso) => all.find((c) => c.iso === iso))
    .filter(Boolean);
  const rest = all
    .filter((c) => !PRIORITY_COUNTRIES.includes(c.iso))
    .sort((a, b) => a.name.localeCompare(b.name));

  return [...priority, ...rest].map((c) => `${c.code} ${c.name}`);
};

export const COUNTRY_CODE_OPTIONS = buildCountryCodeOptions();

const sanitizeSheetName = (name) => {
  const cleaned = String(name || "Template").replace(/[\\/*?[\]:]/g, " ").slice(0, 28);
  return cleaned || "Template";
};

/**
 * Build a list of Project dropdown values from the org's active missions.
 * Always appends General Donation and General Payment.
 */
export const buildProjectOptions = (missions = []) => {
  const active = (missions || [])
    .filter((m) => m && m.status !== "closed" && m.title)
    .map((m) => String(m.title));
  return [...active, GENERAL_DONATION, GENERAL_PAYMENT];
};

export const buildCustomFieldLabels = (organizationObj, language = "en") => {
  return buildCustomFieldMetadata(organizationObj, language).map((f) => f.label);
};

/**
 * Return an array of { label, type, options } for each non-archived custom
 * field, so the template generator can attach dropdowns where appropriate.
 */
export const buildCustomFieldMetadata = (organizationObj, language = "en") => {
  const rows = organizationObj && organizationObj.formSetting && organizationObj.formSetting.rows;
  if (!rows || !rows.length) return [];
  return rows
    .filter((row) => row && !row.archived)
    .map((row) => {
      const label = row.i18n && row.i18n.label && (row.i18n.label[language] || row.i18n.label.en);
      if (!label) return null;
      let options = null;
      if (row.type === "yesno") {
        options = ["Yes", "No"];
      } else if (row.type === "dropdown" && Array.isArray(row.options)) {
        options = row.options
          .map((opt) => {
            if (!opt) return null;
            if (opt.label && typeof opt.label === "object") {
              return opt.label[language] || opt.label.en || null;
            }
            return opt.label || null;
          })
          .filter(Boolean);
      }
      return { label, type: row.type, options };
    })
    .filter(Boolean);
};

/**
 * Generate an .xlsx template as a Blob.
 */
export const generateBulkTemplate = async ({
  organizationObj,
  missions,
  language = "en",
}) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "CheckPlus";
  workbook.created = new Date();

  const orgName = organizationObj && organizationObj.organizationName;
  const sheetName = sanitizeSheetName(orgName ? `${capitalizeWords(orgName)} Bulk` : "Bulk Template");
  const sheet = workbook.addWorksheet(sheetName);

  const customFields = buildCustomFieldMetadata(organizationObj, language);
  const customLabels = customFields.map((f) => f.label);
  const headers = [...BASE_HEADERS, ...customLabels];

  sheet.columns = headers.map((h) => ({
    header: h,
    key: h,
    width: Math.max(14, Math.min(32, h.length + 4)),
  }));
  sheet.getRow(1).font = { bold: true };
  sheet.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE8F0FE" },
  };

  const projectOptions = buildProjectOptions(missions);

  const paymentMethodCol = headers.indexOf("Payment Method") + 1;
  const projectCol = headers.indexOf("Project") + 1;
  const payerTypeCol = headers.indexOf("Payer Type") + 1;
  const paymentStatusCol = headers.indexOf("Payment Status") + 1;
  const colLetter = (n) => {
    let s = "";
    while (n > 0) {
      const m = (n - 1) % 26;
      s = String.fromCharCode(65 + m) + s;
      n = Math.floor((n - 1) / 26);
    }
    return s;
  };

  const paymentMethodRange =
    `${colLetter(paymentMethodCol)}2:${colLetter(paymentMethodCol)}1001`;
  const projectRange =
    `${colLetter(projectCol)}2:${colLetter(projectCol)}1001`;

  sheet.dataValidations.add(paymentMethodRange, {
    type: "list",
    allowBlank: false,
    formulae: [`"${PAYMENT_METHOD_OPTIONS.join(",")}"`],
    showErrorMessage: true,
    errorTitle: "Invalid Payment Method",
    error: "Select a value from the dropdown list.",
  });

  // Project list can be long — store in a hidden reference sheet and reference by range.
  const refSheet = workbook.addWorksheet("Reference");
  refSheet.state = "hidden";
  projectOptions.forEach((value, i) => {
    refSheet.getCell(`A${i + 1}`).value = value;
  });
  const projectRef = `Reference!$A$1:$A$${projectOptions.length}`;
  // Pointer for subsequent lookup lists on the Reference sheet.
  let refRowPointer = projectOptions.length + 2;
  sheet.dataValidations.add(projectRange, {
    type: "list",
    allowBlank: false,
    formulae: [`=${projectRef}`],
    showErrorMessage: true,
    errorTitle: "Invalid Project",
    error: "Select a value from the dropdown list.",
  });

  const payerTypeRange =
    `${colLetter(payerTypeCol)}2:${colLetter(payerTypeCol)}1001`;
  sheet.dataValidations.add(payerTypeRange, {
    type: "list",
    allowBlank: true,
    formulae: [`"${PAYER_TYPE_OPTIONS.join(",")}"`],
    showErrorMessage: true,
    errorTitle: "Invalid Payer Type",
    error: "Select a value from the dropdown list.",
  });

  // Country Code: inline all 245 codes directly into the data validation
  // formula (Excel 2007+ supports up to 32767 chars). Country names from
  // Intl.DisplayNames don't include commas, so plain comma-join is safe.
  const countryCodeCol = headers.indexOf("Country Code") + 1;
  const countryCodeRange =
    `${colLetter(countryCodeCol)}2:${colLetter(countryCodeCol)}1001`;
  sheet.dataValidations.add(countryCodeRange, {
    type: "list",
    allowBlank: true,
    formulae: [`"${COUNTRY_CODE_OPTIONS.join(",")}"`],
    showErrorMessage: true,
    errorTitle: "Invalid Country Code",
    error: "Select a value from the dropdown list.",
  });

  const paymentStatusRange =
    `${colLetter(paymentStatusCol)}2:${colLetter(paymentStatusCol)}1001`;
  sheet.dataValidations.add(paymentStatusRange, {
    type: "list",
    allowBlank: true,
    formulae: [`"${PAYMENT_STATUS_OPTIONS.join(",")}"`],
    showErrorMessage: true,
    errorTitle: "Invalid Payment Status",
    error: "Select a value from the dropdown list.",
  });

  // Date column formatting — pre-fill row 2 with today's date as a real Date
  // value so the column is recognized as date-typed and the user has a visible
  // example to copy-fill from. Backend parses Date values directly (no formula
  // resolution required).
  const txnDateCol = headers.indexOf("Transaction Date") + 1;
  sheet.getColumn(txnDateCol).numFmt = "yyyy-mm-dd hh:mm";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  sheet.getCell(2, txnDateCol).value = today;

  // Data Entry Date — optional. Blank cells fall back to "now" on the server.
  // We format the column as a date and leave row 2 blank so the user sees
  // that it's optional.
  const entryDateCol = headers.indexOf("Data Entry Date") + 1;
  sheet.getColumn(entryDateCol).numFmt = "yyyy-mm-dd hh:mm";

  // Amount column formatting
  const amountCol = headers.indexOf("Amount") + 1;
  sheet.getColumn(amountCol).numFmt = "#,##0.00";

  // Transaction Fee formatting
  const feeCol = headers.indexOf("Transaction Fee") + 1;
  sheet.getColumn(feeCol).numFmt = "#,##0.00";

  // Net = Amount − Transaction Fee. Pre-fill a formula in the first 5 data
  // rows so users see the computed value in Excel. Blank Amount rows render
  // as blank (not 0) via the IF guard. Backend ignores this column — it
  // recomputes net from amount and fee on the server regardless. Users who
  // need more rows can drag-fill the formula; data validation on the other
  // columns extends further so dropdowns still work on pasted rows.
  const netCol = headers.indexOf("Net") + 1;
  sheet.getColumn(netCol).numFmt = "#,##0.00";
  const amountLetter = colLetter(amountCol);
  const feeLetter = colLetter(feeCol);
  for (let r = 2; r <= 6; r++) {
    const cell = sheet.getCell(r, netCol);
    cell.value = {
      formula: `IF(${amountLetter}${r}="","",${amountLetter}${r}-IFERROR(${feeLetter}${r},0))`,
      result: ""
    };
  }

  // Custom field dropdowns (yesno + single-select dropdown types)
  customFields.forEach((field, idx) => {
    if (!field.options || !field.options.length) return;
    const colIdx = BASE_HEADERS.length + idx + 1;
    const range = `${colLetter(colIdx)}2:${colLetter(colIdx)}1001`;

    // Short option lists use the inline formulae form; longer ones
    // go on the hidden Reference sheet (Excel limits inline lists to ~255 chars).
    const inline = field.options.join(",");
    if (inline.length < 250 && !inline.includes('"')) {
      sheet.dataValidations.add(range, {
        type: "list",
        allowBlank: true,
        formulae: [`"${inline}"`],
        showErrorMessage: true,
        errorTitle: `Invalid ${field.label}`,
        error: "Select a value from the dropdown list.",
      });
    } else {
      const startRow = refRowPointer;
      field.options.forEach((opt, i) => {
        refSheet.getCell(`A${startRow + i}`).value = opt;
      });
      const endRow = startRow + field.options.length - 1;
      sheet.dataValidations.add(range, {
        type: "list",
        allowBlank: true,
        formulae: [`=Reference!$A$${startRow}:$A$${endRow}`],
        showErrorMessage: true,
        errorTitle: `Invalid ${field.label}`,
        error: "Select a value from the dropdown list.",
      });
      refRowPointer = endRow + 2;
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const downloadBulkTemplate = async (opts) => {
  const blob = await generateBulkTemplate(opts);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `bulk-upload-template-${stamp}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
